import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTenantTranslation } from "hooks/formatters";
import { useTenantData } from "contexts/tenant";
import { useNotifyStatus } from "contexts/status";
import { useUploadFile } from "src/components/file/useUploadFile";
import { FileExplorerProvider } from "src/components/file/FileExplorerContext";
import {
  useRecentInventoryFiles,
  getTodayStartInTenantLocalTime,
} from "./useRecentInventoryFiles";
import { FileUploadCard, FileUploadStatus } from "./FileUploadCard";
import type { UploadedFileType } from "components/file/upload/uploadedFileType";

const POLL_INTERVAL_MS = 3000;
const UPLOAD_PROCESSING_DELAY_MS = 2000;

interface InventoryMeasurementUploadGatewayProps {
  children: React.ReactNode;
}

export const InventoryMeasurementUploadGateway: React.FC<
  InventoryMeasurementUploadGatewayProps
> = ({ children }) => {
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const { handleUpload, uploadLoading } = useUploadFile();
  const { local_timezone: localTimezone } = useTenantData();

  const [showUploadInterface, setShowUploadInterface] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  const todayStart = getTodayStartInTenantLocalTime(localTimezone);

  const {
    fileStatuses,
    allFilesPresent,
    allFilesProcessed,
    isLoading,
    refetch,
  } = useRecentInventoryFiles(todayStart);

  // Check if we should show the upload interface or the form
  useEffect(() => {
    if (!isLoading) {
      if (!allFilesPresent && !showUploadInterface) {
        // No recent files, show upload interface
        // eslint-disable-next-line @eslint-react/set-state-in-effect -- upload flow state machine transition
        setShowUploadInterface(true);
      }
    }
  }, [isLoading, allFilesPresent, showUploadInterface]);

  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [isPolling, refetch]);

  useEffect(() => {
    if (isPolling && allFilesProcessed) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- stops polling after async processing completes
      setIsPolling(false);
      notifyStatus({
        text: t("allFilesProcessed"),
        type: "success",
      });
    }
  }, [isPolling, allFilesProcessed, notifyStatus, t]);

  const handleFilesDrop = useCallback(
    async (files: FileList) => {
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          await handleUpload(file, "adhoc" as UploadedFileType);
          return { fileName: file.name, success: true };
        } catch {
          return { fileName: file.name, success: false };
        }
      });

      const results = await Promise.all(uploadPromises);

      results.forEach(({ fileName, success }) => {
        notifyStatus({
          text: success
            ? `${t("uploadFileSuccess")}: ${fileName}`
            : `${t("failedToUploadFile")}: ${fileName}`,
          type: success ? "success" : "error",
        });
      });

      setTimeout(() => {
        refetch();
        setIsPolling(true);
      }, UPLOAD_PROCESSING_DELAY_MS);
    },
    [handleUpload, notifyStatus, refetch, t]
  );

  const handleProceedWithRecentFiles = () => {
    if (allFilesProcessed) {
      setShowForm(true);
    } else {
      setIsPolling(true);
    }
  };

  const handleUploadNewFiles = () => {
    setShowUploadInterface(true);
  };

  const handleContinueToForm = () => {
    if (!allFilesPresent || !allFilesProcessed) {
      setShowValidationError(true);
      return;
    }
    setShowForm(true);
  };

  const getFileStatus = (fileType: UploadedFileType): FileUploadStatus => {
    const status = fileStatuses.find((s) => s.fileType === fileType);
    if (!status) return "pending";

    if (!status.isPresent) return "pending";
    if (status.isProcessed) return "processed";
    return "processing";
  };

  const hasFilesProcessing = fileStatuses.some(
    (status) => status.isPresent && !status.isProcessed
  );

  // If form should be shown, render children
  if (showForm) {
    return children;
  }

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          gap: 3,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  // Show recent files option
  if (allFilesPresent && !showUploadInterface) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3} maxWidth="900px" mx="auto">
          <Typography variant="h4" gutterBottom>
            {t("uploadInventoryFiles")}
          </Typography>

          <Alert severity="info">{t("recentFilesFound")}</Alert>

          <Stack spacing={2}>
            {fileStatuses.map((status) => (
              <FileUploadCard
                key={status.fileType}
                fileType={status.fileType}
                file={status.file}
                status={getFileStatus(status.fileType)}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="center">
            {allFilesProcessed ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircleIcon />}
                onClick={handleProceedWithRecentFiles}
              >
                {t("continueToForm")}
              </Button>
            ) : (
              <LoadingButton
                variant="contained"
                size="large"
                onClick={handleProceedWithRecentFiles}
                loading={isPolling}
              >
                {isPolling
                  ? t("waitingForProcessing")
                  : t("proceedWithRecentFiles")}
              </LoadingButton>
            )}
            <Button
              variant="outlined"
              size="large"
              onClick={handleUploadNewFiles}
            >
              {t("uploadNewFiles")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Show upload interface
  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3} maxWidth="900px" mx="auto">
        <Typography variant="h4" gutterBottom>
          {t("uploadInventoryFiles")}
        </Typography>

        <Alert severity="info">{t("uploadThreeFilesRequired")}</Alert>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "2px dashed",
            borderColor: "divider",
            backgroundColor: "background.default",
          }}
        >
          <FileExplorerProvider>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CloudUploadIcon color="action" />
                <Typography variant="h6">{t("uploadFiles")}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("dragAndDropOrClick")}
              </Typography>
              <Box
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void handleFilesDrop(e.dataTransfer.files);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <input
                  type="file"
                  multiple
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    if (e.target.files) {
                      void handleFilesDrop(e.target.files);
                    }
                  }}
                  style={{ display: "none" }}
                  id="file-upload-input"
                />
                <label htmlFor="file-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    disabled={uploadLoading}
                    startIcon={
                      uploadLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                  >
                    {uploadLoading ? t("uploading") : t("selectFiles")}
                  </Button>
                </label>
              </Box>
            </Stack>
          </FileExplorerProvider>
        </Paper>

        <Stack spacing={2}>
          {fileStatuses.map((status) => (
            <FileUploadCard
              key={status.fileType}
              fileType={status.fileType}
              file={status.file}
              status={getFileStatus(status.fileType)}
            />
          ))}
        </Stack>

        {hasFilesProcessing ? (
          <Alert severity="info" icon={<CircularProgress size={20} />}>
            {t("waitingForProcessing")}
          </Alert>
        ) : null}

        {showValidationError && !allFilesPresent ? (
          <Alert severity="error">{t("missingFileTypes")}</Alert>
        ) : null}

        {showValidationError && allFilesPresent && !allFilesProcessed ? (
          <Alert severity="error">{t("waitingForProcessing")}</Alert>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <LoadingButton
            variant="contained"
            size="large"
            onClick={handleContinueToForm}
            loading={hasFilesProcessing}
            startIcon={
              allFilesProcessed ? <CheckCircleIcon /> : <CloudUploadIcon />
            }
          >
            {t("continueToForm")}
          </LoadingButton>
        </Box>
      </Stack>
    </Box>
  );
};
